import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMXN } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { CalendarDays, RefreshCcw } from "lucide-react";

const COLORS = ["#FF5A1F", "#FACC15", "#10B981", "#3B82F6", "#A855F7", "#EC4899"];

// Helpers: input type="date" funciona en local; convertimos al UTC ISO completo
// del día (00:00:00 inicio, 23:59:59 fin) usando la zona del navegador.
const ymdLocal = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const startIso = (ymd) => new Date(`${ymd}T00:00:00`).toISOString();
const endIso = (ymd) => new Date(`${ymd}T23:59:59.999`).toISOString();

export default function Reportes() {
  const hoy = ymdLocal();
  const [desde, setDesde] = useState(hoy);
  const [hasta, setHasta] = useState(hoy);
  const [applied, setApplied] = useState({ desde: hoy, hasta: hoy });
  const [corte, setCorte] = useState(null);
  const [serie, setSerie] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (rango) => {
    setLoading(true);
    try {
      const params = { desde: startIso(rango.desde), hasta: endIso(rango.hasta) };
      const [c, s] = await Promise.all([
        api.get("/reportes/corte", { params }),
        api.get("/reportes/serie", { params }),
      ]);
      setCorte(c.data);
      setSerie(s.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(applied); }, [load, applied]);

  const aplicar = () => setApplied({ desde, hasta });
  const irHoy = () => {
    setDesde(hoy); setHasta(hoy);
    setApplied({ desde: hoy, hasta: hoy });
  };

  const rangoLabel = useMemo(() => {
    if (applied.desde === applied.hasta) return `Día: ${applied.desde}`;
    return `Rango: ${applied.desde} → ${applied.hasta}`;
  }, [applied]);

  const ventasPorMetodo = corte?.ventas_por_metodo || [];
  const topProductos = corte?.top_productos || [];
  const serieData = serie?.data || [];
  const sinVentas = !loading && (corte?.total_ventas || 0) === 0 && serieData.length === 0;

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-black tracking-tight">Reportes</h2>
          <p className="text-sm text-muted-foreground mt-1">
            <CalendarDays className="inline h-3.5 w-3.5 mr-1 -mt-0.5" />{rangoLabel}
          </p>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="grid gap-3 sm:grid-cols-4 items-end">
            <div>
              <Label className="text-xs">Fecha inicio</Label>
              <Input type="date" value={desde} max={hasta} onChange={(e) => setDesde(e.target.value)} data-testid="filtro-desde" />
            </div>
            <div>
              <Label className="text-xs">Fecha fin</Label>
              <Input type="date" value={hasta} min={desde} onChange={(e) => setHasta(e.target.value)} data-testid="filtro-hasta" />
            </div>
            <Button onClick={aplicar} disabled={loading} data-testid="aplicar-filtros-btn">Aplicar filtros</Button>
            <Button variant="outline" onClick={irHoy} disabled={loading} data-testid="hoy-btn">
              <RefreshCcw className="h-4 w-4 mr-1" />Hoy
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card><CardContent className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Ventas</div><div className="text-3xl font-black text-primary mt-2" data-testid="kpi-ventas">{fmtMXN(corte?.total_ventas || 0)}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Gastos</div><div className="text-3xl font-black text-destructive mt-2" data-testid="kpi-gastos">{fmtMXN(corte?.total_gastos || 0)}</div></CardContent></Card>
        <Card><CardContent className="p-5"><div className="text-xs uppercase tracking-widest text-muted-foreground">Neto</div><div className="text-3xl font-black text-emerald-500 mt-2" data-testid="kpi-neto">{fmtMXN(corte?.neto || 0)}</div></CardContent></Card>
      </div>

      {sinVentas ? (
        <Card><CardContent className="p-8 text-center text-muted-foreground" data-testid="empty-state">
          Sin ventas en este rango.
        </CardContent></Card>
      ) : (
        <>
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardContent className="p-5">
                <h3 className="text-lg font-bold mb-3">Métodos de pago</h3>
                {ventasPorMetodo.length === 0 ? (
                  <div className="text-sm text-muted-foreground" data-testid="metodo-empty">Sin ventas en este rango.</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={ventasPorMetodo} dataKey="total" nameKey="_id" outerRadius={80} label>
                        {ventasPorMetodo.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtMXN(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5">
                <h3 className="text-lg font-bold mb-3">Top productos</h3>
                {topProductos.length === 0 ? (
                  <div className="text-sm text-muted-foreground" data-testid="top-empty">Sin productos vendidos en este rango.</div>
                ) : (
                  <>
                    {/* Gráfica horizontal — complementa la lista, no la reemplaza. */}
                    <div data-testid="top-productos-chart" className="mb-4">
                      <ResponsiveContainer width="100%" height={Math.max(180, topProductos.length * 28)}>
                        <BarChart
                          data={topProductos.slice(0, 8).map((p, i) => ({
                            name: (p._id || "Producto eliminado").length > 22 ? (p._id || "Producto eliminado").slice(0, 22) + "…" : (p._id || "Producto eliminado"),
                            cantidad: p.cantidad,
                            total: p.total,
                            fill: COLORS[i % COLORS.length],
                          }))}
                          layout="vertical"
                          margin={{ top: 4, right: 12, bottom: 4, left: 8 }}
                          barCategoryGap={6}
                        >
                          <XAxis type="number" hide />
                          <YAxis
                            type="category"
                            dataKey="name"
                            tick={{ fontSize: 11, fill: "currentColor" }}
                            width={140}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{ backgroundColor: "var(--card)", border: "1px solid var(--border)", borderRadius: 8 }}
                            formatter={(value, name) => name === "cantidad" ? [`x${value}`, "Cantidad"] : [fmtMXN(value), "Total"]}
                          />
                          <Bar dataKey="cantidad" radius={[0, 6, 6, 0]}>
                            {topProductos.slice(0, 8).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Lista detallada existente */}
                    <div className="space-y-2" data-testid="top-productos-list">
                      {topProductos.slice(0, 8).map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm border-b border-border last:border-0 pb-1">
                          <span className="inline-flex items-center gap-2 flex-1 min-w-0 mr-2">
                            <span className="h-2.5 w-2.5 rounded-sm shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                            <span className="font-semibold truncate">{p._id || "Producto eliminado"}</span>
                          </span>
                          <span className="text-muted-foreground mr-3 text-xs">x{p.cantidad}</span>
                          <span className="font-bold">{fmtMXN(p.total)}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardContent className="p-5">
              <h3 className="text-lg font-bold mb-3">
                Ventas {serie?.granularidad === "hora" ? "por hora" : "por día"}
              </h3>
              {serieData.length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="serie-empty">Sin ventas en este rango.</div>
              ) : serie?.granularidad === "hora" ? (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={serieData}>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip formatter={(v) => fmtMXN(v)} />
                    <Bar dataKey="total" fill="#FF5A1F" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={serieData}>
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis />
                    <Tooltip formatter={(v) => fmtMXN(v)} />
                    <Line type="monotone" dataKey="total" stroke="#FF5A1F" strokeWidth={3} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Card><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground">Órdenes canceladas</div><div className="text-2xl font-black mt-1" data-testid="kpi-canceladas">{corte?.canceladas || 0}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-xs uppercase tracking-widest text-muted-foreground">Órdenes devueltas</div><div className="text-2xl font-black mt-1" data-testid="kpi-devueltas">{corte?.devueltas || 0}</div></CardContent></Card>
      </div>
    </div>
  );
}
